import { Button } from "@/components/flexkit/Button";
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { ExternalLink } from "@/components/podkit/typography/ExternalLink";
import { Text } from "@/components/podkit/typography/Text";
import { AWSRunnerAdditionalFieldsKeys } from "@/components/runners/details/runner-configuration-keys";
import { TrackLocations } from "@/hooks/use-segment";
import type { PlainRunner } from "@/queries/runner-queries";
import { useCallback, type FC } from "react";

type Props = {
    runner: PlainRunner;
    open: boolean;
    onClose: () => void;
};

const UPGRADE_TEMPLATE_URLS = {
    standard:
        "https://gitpod-flex-releases.s3.amazonaws.com/ec2/stable/gitpod-ec2-runner-enterprise-with-custom-network.json",
    privateEcr:
        "https://gitpod-flex-releases.s3.amazonaws.com/ec2/stable/gitpod-ec2-runner-enterprise-with-custom-network-private-ecr.template.json",
} as const;

export const UpgradeRunnerModal: FC<Props> = ({ open, runner, onClose }) => {
    const handleOpenChange = useCallback(
        (nextOpen: boolean) => {
            if (!nextOpen) {
                onClose();
            }
        },
        [onClose],
    );

    const dict = Object.fromEntries(runner.status?.additionalInfo.map((info) => [info.key, info.value]) || []);
    const stackURL = dict[AWSRunnerAdditionalFieldsKeys.StackURL] || "";

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent className="max-w-xl" data-track-location={TrackLocations.UpgradeRunnerModal}>
                <DialogHeader>
                    <DialogTitle>Upgrade runner</DialogTitle>
                    <DialogDescription>
                        Upgrade your AWS runner by updating the CloudFormation stack with a new template.
                    </DialogDescription>
                </DialogHeader>

                <DialogBody>
                    <div className="flex flex-col gap-4">
                        <Text>To perform an in-place upgrade of your runner:</Text>

                        <ol className="list-decimal space-y-3 pl-6">
                            <li>
                                <Text>
                                    <span>Open your runner&apos;s </span>
                                    {stackURL ? (
                                        <ExternalLink href={stackURL} className="font-bold">
                                            CloudFormation stack
                                        </ExternalLink>
                                    ) : (
                                        <span>CloudFormation stack</span>
                                    )}
                                    <span> in the AWS Console</span>
                                </Text>
                            </li>
                            <li>
                                <Text>
                                    Click <strong>Update</strong> → <strong>Replace existing template</strong>
                                </Text>
                            </li>
                            <li>
                                <Text>Enter the template URL that matches your setup:</Text>
                                <div className="mt-2 space-y-2">
                                    <div>
                                        <Text className="text-sm font-medium">Standard:</Text>
                                        <code className="mt-1 block break-all rounded bg-surface-secondary p-2 text-sm">
                                            {UPGRADE_TEMPLATE_URLS.standard}
                                        </code>
                                    </div>
                                    <div>
                                        <Text className="text-sm font-medium">With private ECR:</Text>
                                        <code className="mt-1 block break-all rounded bg-surface-secondary p-2 text-sm">
                                            {UPGRADE_TEMPLATE_URLS.privateEcr}
                                        </code>
                                    </div>
                                </div>
                            </li>
                            <li>
                                <Text>Review parameters and complete the wizard</Text>
                            </li>
                        </ol>

                        <div className="mt-2 rounded-lg border border-border-light bg-surface-secondary p-3">
                            <Text className="text-sm">
                                <strong>Note:</strong> Templates from January 2025 or earlier have SSH port changes.
                                Before upgrading, stop and discard existing environments, or add port 22 to your
                                security group.
                            </Text>
                        </div>

                        <Text className="mt-2">
                            <span>For detailed instructions, see the </span>
                            <ExternalLink
                                href="https://ona.com/docs/ona/runners/aws/update-runner"
                                className="font-bold"
                            >
                                runner upgrade documentation
                            </ExternalLink>
                            <span>.</span>
                        </Text>
                    </div>
                </DialogBody>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button
                            type="button"
                            variant="secondary"
                            data-tracking-id="close-upgrade-runner-modal"
                        >
                            Close
                        </Button>
                    </DialogClose>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
